// lib/workspace-config.js — WORKSPACE CONSTANTS
// ═══════════════════════════════════════════════════════════
// Single source of truth for Workspace feature limits, node types,
// colors, and layout defaults. Tune here, affects the whole feature.
//
// When subscription tiers ship, the file limit becomes a lookup
// function getFileLimit(userTier) instead of a constant. Until then
// it's a single global limit that matches Claude.ai's conversation
// file limit.
// ═══════════════════════════════════════════════════════════

// ── File upload limits ──────────────────────────────────────
// Hard cap on files per workspace. Includes both uploaded files and
// Shift-generated artifacts. Enforced server-side in the file-node
// creation path AND client-side as a pre-check for UX.
export const WORKSPACE_FILE_LIMIT = 20;

// Warning threshold — UI shows a yellow indicator at or above this.
export const WORKSPACE_FILE_WARNING = 15;

// Future: replace with getFileLimit(userTier) when tiers ship.
// export function getFileLimit(tier) {
//   return { free: 20, pro: 100, enterprise: 500 }[tier] || 20;
// }

// ── Node types ──────────────────────────────────────────────
// Every node in workspace_nodes has a type. Moment types are
// created by the Haiku classifier. File type is created when an
// artifact is added. Root is created once when the workspace opens.
export const NODE_TYPES = {
  ROOT:      'root',       // Session goal, one per workspace
  MILESTONE: 'milestone',  // Significant completion / turning point
  QUESTION:  'question',   // Unresolved question raised in the session
  DECISION:  'decision',   // Choice made or direction committed
  INSIGHT:   'insight',    // Realization or breakthrough
  FILE:      'file',       // Uploaded or Shift-generated artifact
};

// ── Node colors ─────────────────────────────────────────────
// Visual language for the journey map. Matches the Workspace color
// system decided earlier:
//   Milestone = teal   (completion, forward momentum)
//   Question  = sage   (open, inviting exploration)
//   Decision  = gold   (weight, commitment, structural)
//   Insight   = purple (depth, breakthrough, reflective)
//   Root      = gold   (anchor, the goal)
//   File      = neutral + gold tint if Shift-generated
export const NODE_COLORS = {
  [NODE_TYPES.ROOT]:      '#c8a96e', // gold — the anchor
  [NODE_TYPES.MILESTONE]: '#4db8b8', // teal — completion
  [NODE_TYPES.QUESTION]:  '#7a9e82', // sage — open
  [NODE_TYPES.DECISION]:  '#c8a96e', // gold — commitment
  [NODE_TYPES.INSIGHT]:   '#9b8fc4', // purple — depth
  [NODE_TYPES.FILE]:      '#888888', // neutral, overridden by file_source
};

// File node color by source
export const FILE_NODE_COLORS = {
  uploaded:  '#888888', // neutral gray — user brought it in
  generated: '#c8a96e', // gold tint — Shift created it
};

// ── Node status ─────────────────────────────────────────────
export const NODE_STATUS = {
  BUILDING:   'building',    // Shift generating this right now, gold pulse
  ACTIVE:     'active',      // currently open in canvas, gold border
  REFERENCED: 'referenced',  // mentioned in chat this session, purple
  RELATED:    'related',     // topic-linked to the active canvas file, sage
  IDLE:       'idle',        // exists, not currently involved
  ERROR:      'error',       // failed to generate or save, terracotta
  PAST:       'past',        // completed or parked, fades (legacy compat)
  FUTURE:     'future',      // Shift-suggested next step, dashed outline
  FOLDED:     'folded',      // sub-branch collapsed (Phase 3+)
};

// Node status → visual treatment for journey map
export const NODE_STATE_COLORS = {
  building:   { border: 'rgba(200,169,110,0.25)', bg: 'rgba(200,169,110,0.08)', text: '#c8a96e' },
  active:     { border: 'rgba(200,169,110,0.25)', bg: 'rgba(200,169,110,0.08)', text: '#c8a96e' },
  referenced: { border: 'rgba(155,143,196,0.25)', bg: 'rgba(155,143,196,0.08)', text: '#9b8fc4' },
  related:    { border: 'rgba(122,158,130,0.25)', bg: 'rgba(122,158,130,0.08)', text: '#7a9e82' },
  idle:       { border: '#141414',                bg: 'transparent',             text: '#555' },
  error:      { border: 'rgba(196,96,80,0.25)',   bg: 'rgba(196,96,80,0.08)',   text: '#c46050' },
  past:       { border: '#1e1e2a',                bg: 'transparent',             text: '#555' },
  future:     { border: '#1e1e2a',                bg: 'transparent',             text: '#444' },
  folded:     { border: '#141414',                bg: 'transparent',             text: '#333' },
};

// ── Workstation layout defaults ─────────────────────────────
// Default panel widths when a workspace session opens. Users can
// drag handles to change. Layout presets override these.
export const WORKSTATION_LAYOUT = {
  // Default: 50/50 chat + canvas, map collapsed to rail
  DEFAULT: {
    chatPct: 50,
    canvasPct: 50,
    mapPct: 0,
    mapCollapsed: true,
  },
  // Map expanded: compress chat + canvas to 33% each
  MAP_OPEN: {
    chatPct: 33,
    canvasPct: 34,
    mapPct: 33,
    mapCollapsed: false,
  },
  // Chat-heavy preset
  CHAT: {
    chatPct: 60,
    canvasPct: 40,
    mapPct: 0,
    mapCollapsed: true,
  },
  // Canvas-heavy preset
  CANVAS: {
    chatPct: 25,
    canvasPct: 75,
    mapPct: 0,
    mapCollapsed: true,
  },
  // Map-focused preset
  MAP: {
    chatPct: 25,
    canvasPct: 25,
    mapPct: 50,
    mapCollapsed: false,
  },
};

// Rail width when the map is collapsed (narrow strip on far right)
export const MAP_RAIL_WIDTH_PX = 40;

// ── Haiku classifier prompt config ──────────────────────────
// See pages/api/workspace/classify-moment.js for the actual prompt.
// These constants control the classifier's behavior.
export const CLASSIFIER = {
  // Minimum words in a Shift response before we bother classifying.
  // Short responses (< 20 words) are almost always clarifications,
  // not worth node creation.
  MIN_WORDS: 20,

  // Max tokens for the classifier response. We only need a few words.
  MAX_TOKENS: 80,

  // If Haiku returns this value, no node is created
  NONE_VALUE: 'none',
};
